import React, { useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import * as SC from "./purchase-subscription.style";
import { CustomAlert } from "lib/components/ui";
import {
  Storage,
  AccessTime,
  CheckCircle,
  Upload,
  ContentCopy,
  ArrowForward,
} from "@mui/icons-material";
import { useReadPaymentSettingsQuery } from "api/endpoints/settings";
import { useReadAvailableVmessCategoriesQuery } from "api/endpoints/vmess-category";
import { useReadAvailableSshCategoriesQuery } from "api/endpoints/ssh-category";
import {
  useCreateSubscriptionMutation,
  useRenewSubscriptionMutation,
  useDeleteSubscriptionMutation,
} from "api/endpoints/subscription";
import { useUploadPaymentReceiptMutation } from "api/endpoints/payment-receipt";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { ROUTE_PATH } from "router/router-constant/router.constant";
import { formatCategoryLimit } from "pages/settings/vpn/vpn-settings.utils";
import {
  SUBSCRIPTION_PLAN_TYPES,
  getPlanDisplayName,
  getPlanDefaults,
  SubscriptionPlanType,
  VpnProviderType,
  isInventoryProviderType,
} from "@dots-crm/contracts/client";

const planTypes = [
  {
    id: SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED,
    title: getPlanDisplayName(SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED),
    description: "مناسب برای استفاده روزانه معمولی",
    icon: <Storage />,
    features: ["حجم محدود ماهانه", "بدون محدودیت زمانی", "پشتیبانی 24/7"],
  },
  {
    id: SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT,
    title: getPlanDisplayName(SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT),
    description: "مناسب برای استفاده فشرده در مدت کوتاه",
    icon: <AccessTime />,
    features: ["حجم نامحدود", "محدودیت زمانی (1-6 ماه)", "سرعت بالا"],
  },
  {
    id: SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT,
    title: getPlanDisplayName(SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT),
    description: "مناسب برای استفاده خاص و کوتاه مدت",
    icon: <CheckCircle />,
    features: ["حجم محدود", "زمان محدود", "قیمت اقتصادی"],
  },
];


// Helper functions to generate options for each plan type
const getGigLimitOptions = () => {
  const options = [];
  for (let i = 10; i <= 100; i += 10) {
    options.push({ value: i, label: `${i} گیگابایت` });
  }
  return options;
};

const getTimeLimitOptions = () => {
  return [
    { value: 1, label: "1 ماه" },
    { value: 2, label: "2 ماه" },
    { value: 3, label: "3 ماه" },
    { value: 4, label: "4 ماه" },
    { value: 5, label: "5 ماه" },
    { value: 6, label: "6 ماه" },
  ];
};

export const PurchaseSubscriptionComponent = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [providerType, setProviderType] = useState<VpnProviderType>("V2RAY");
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [selectedManualCategoryId, setSelectedManualCategoryId] = useState("");
  const [gigLimit, setGigLimit] = useState<number>(0);
  const [timeLimit, setTimeLimit] = useState<number>(0);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  const { data: settings, isLoading: settingsLoading } =
    useReadPaymentSettingsQuery();
  const { data: vmessCategoriesData, isLoading: vmessCategoriesLoading } =
    useReadAvailableVmessCategoriesQuery({}, {
      skip: providerType !== "VMESS",
    });
  const { data: sshCategoriesData, isLoading: sshCategoriesLoading } =
    useReadAvailableSshCategoriesQuery({}, {
      skip: providerType !== "SSH",
    });
  const inventoryCategoriesData =
    providerType === "VMESS"
      ? vmessCategoriesData
      : providerType === "SSH"
      ? sshCategoriesData
      : undefined;
  const categoriesLoading =
    providerType === "VMESS"
      ? vmessCategoriesLoading
      : providerType === "SSH"
      ? sshCategoriesLoading
      : false;
  const [createSubscription, { isLoading: creating }] =
    useCreateSubscriptionMutation();
  const [renewSubscription, { isLoading: renewing }] =
    useRenewSubscriptionMutation();
  const [uploadReceipt, { isLoading: uploading }] =
    useUploadPaymentReceiptMutation();
  const [deleteSubscription] = useDeleteSubscriptionMutation();

  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is a renew operation
  const isRenewMode = new URLSearchParams(location.search).get('renew') === 'true';
  
  // Define steps based on mode
  const steps = isRenewMode 
    ? ["انتخاب پلن", "اطلاعات پرداخت", "آپلود فیش"]
    : ["انتخاب پلن", "اطلاعات پرداخت", "آپلود فیش"];
  
  // Pre-fill form when in renew mode
  React.useEffect(() => {
    if (isRenewMode) {
      const urlParams = new URLSearchParams(location.search);
      const planType = urlParams.get('planType');
      const gigLimitParam = urlParams.get('gigLimit');
      const timeLimitParam = urlParams.get('timeLimit');
      
      if (planType) {
        setSelectedPlan(planType as SubscriptionPlanType);
      }
      if (gigLimitParam) {
        setGigLimit(parseInt(gigLimitParam, 10));
      }
      if (timeLimitParam) {
        setTimeLimit(parseInt(timeLimitParam, 10));
      }
    }
  }, [isRenewMode, location.search]);

  // Helper function to format card number with dashes
  const formatCardNumber = (cardNumber: string): string => {
    if (!cardNumber) return "";
    // Remove all non-digit characters
    const digitsOnly = cardNumber.replace(/\D/g, "");
    // Add dashes every 4 digits
    return digitsOnly.replace(/(\d{4})(?=\d)/g, "$1-");
  };

  // Helper function to copy card number to clipboard
  const copyCardNumber = async (cardNumber: string) => {
    try {
      await navigator.clipboard.writeText(cardNumber);
      toast.success("شماره کارت کپی شد");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = cardNumber;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("شماره کارت کپی شد");
    }
  };

  // Check if selected plan is still enabled, reset if disabled
  React.useEffect(() => {
    if (selectedPlan && settings) {
      let isPlanEnabled = true;

      switch (selectedPlan) {
        case SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED:
          isPlanEnabled = (settings as any).gigLimitTimeUnlimitedEnabled;
          break;
        case SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT:
          isPlanEnabled = (settings as any).gigUnlimitedTimeLimitEnabled;
          break;
        case SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT:
          isPlanEnabled = (settings as any).gigLimitTimeLimitEnabled;
          break;
      }

      if (!isPlanEnabled) {
        setSelectedPlan("");
        setGigLimit(0);
        setTimeLimit(0);
      }
    }
  }, [selectedPlan, settings]);

  const enabledProviders: VpnProviderType[] = [];
  if (settings?.v2rayEnabled ?? true) enabledProviders.push("V2RAY");
  if (settings?.vmessEnabled ?? true) enabledProviders.push("VMESS");
  if (settings?.sshEnabled ?? true) enabledProviders.push("SSH");

  React.useEffect(() => {
    if (settings && !enabledProviders.includes(providerType)) {
      setProviderType(enabledProviders[0] || "V2RAY");
      setSelectedManualCategoryId("");
      setSelectedPlan("");
    }
  }, [settings, providerType]);

  const selectedCategory = inventoryCategoriesData?.categories.find(
    (c) => c._id === selectedManualCategoryId
  );
  const isSelectedCategoryOutOfStock =
    isInventoryProviderType(providerType) &&
    selectedCategory?.availableCount === 0;

  const getCurrentPrice = (targetPlan?: SubscriptionPlanType) => {
    if (isInventoryProviderType(providerType)) {
      const category = inventoryCategoriesData?.categories.find(
        (c) => c._id === selectedManualCategoryId
      );
      return category?.price || 0;
    }
    if (!settings) return 0;
    const plan = targetPlan || selectedPlan;
    switch (plan) {
      case SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED:
        return settings.gigLimitTimeUnlimitedPrice * (gigLimit || 0);
      case SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT:
        return settings.gigUnlimitedTimeLimitPrice * (timeLimit || 0);
      case SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT:
        return settings.gigLimitTimeLimitPrice * (gigLimit || 0);
      default:
        return 0;
    }
  };

  const getBasePrice = (targetPlan?: SubscriptionPlanType) => {
    if (!settings) return 0;
    switch (targetPlan || selectedPlan) {
      case SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED:
        return settings.gigLimitTimeUnlimitedPrice;
      case SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT:
        return settings.gigUnlimitedTimeLimitPrice;
      case SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT:
        return settings.gigLimitTimeLimitPrice;
      default:
        return 0;
    }
  };

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    // Set default values based on plan type
    const defaults = getPlanDefaults(planId as SubscriptionPlanType);
    setGigLimit(defaults.gigLimit);

    // For GIG_LIMIT_TIME_LIMIT, always set time limit to 1
    if (planId === SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT) {
      setTimeLimit(1);
    } else {
      setTimeLimit(defaults.timeLimit);
    }
  };

  const handleNext = async () => {
    if (activeStep < 2) {
      setActiveStep(activeStep + 1);
    } else if (activeStep === 2) {
      const handleCreateSubscription = async () => {
        const subscription = isRenewMode
          ? await renewSubscription({
              payload: {
                gigLimit: gigLimit || 0,
                timeLimit: timeLimit || 0,
                planType: selectedPlan,
                price: getCurrentPrice(),
                providerType,
                manualCategoryId: isInventoryProviderType(providerType)
                  ? selectedManualCategoryId
                  : undefined,
              },
            }).unwrap()
          : await createSubscription({
              payload: {
                gigLimit: isInventoryProviderType(providerType)
                  ? selectedCategory?.gigLimit || 0
                  : gigLimit || 0,
                timeLimit: isInventoryProviderType(providerType)
                  ? selectedCategory?.timeLimit || 0
                  : timeLimit || 0,
                planType: isInventoryProviderType(providerType)
                  ? SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT
                  : selectedPlan,
                price: getCurrentPrice(),
                providerType,
                manualCategoryId: isInventoryProviderType(providerType)
                  ? selectedManualCategoryId
                  : undefined,
              },
            }).unwrap();

        const subscriptionId = subscription?.subscriptionId;

        if (receiptFile && subscriptionId) {
          try {
            await uploadReceipt({
              payload: { subscriptionId, price: getCurrentPrice() },
              file: receiptFile,
            }).unwrap();

            navigate(ROUTE_PATH.subscriptions.mySubscription.path);
          } catch (error) {
            await deleteSubscription({
              params: { subscriptionId },
            }).unwrap();
          }
        }
      };

      await toast.promise(handleCreateSubscription, {
        pending: isRenewMode ? "در حال تمدید اشتراک..." : "در حال ایجاد اشتراک...",
        success: isRenewMode ? "اشتراک با موفقیت تمدید شد." : "اشتراک با موفقیت ایجاد شد.",
        error: isRenewMode ? "خطا در تمدید اشتراک. لطفاً دوباره تلاش کنید." : "خطا در ایجاد اشتراک. لطفاً دوباره تلاش کنید.",
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  if (
    settingsLoading ||
    (isInventoryProviderType(providerType) && categoriesLoading)
  ) {
    return (
      <SC.LoadingContainer>
        <CircularProgress />
      </SC.LoadingContainer>
    );
  }

  return (
    <SC.PurchaseSubscriptionContainer>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <ArrowForward 
          onClick={() => navigate(ROUTE_PATH.subscriptions.mySubscription.path)}
          sx={{ 
            cursor: "pointer", 
            fontSize: 28,
            color: "primary.main",
            "&:hover": {
              color: "primary.dark"
            }
          }}
        />
        <Typography variant="h4" sx={{ flex: 1 }}>
          {isRenewMode ? "تمدید اشتراک" : "خرید اشتراک جدید"}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3, boxShadow: 'none' }}>
        <SC.StepperContainer>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </SC.StepperContainer>

        {/* Step 1: Plan Selection */}
        {activeStep === 0 && (
          <SC.PlanSelectionContainer>
            {enabledProviders.length === 0 ? (
              <CustomAlert severity="info">
                در حال حاضر محصولی برای فروش وجود ندارد.
              </CustomAlert>
            ) : (
              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                {enabledProviders.includes("V2RAY") && (
                  <Button
                    variant={providerType === "V2RAY" ? "contained" : "outlined"}
                    onClick={() => {
                      setProviderType("V2RAY");
                      setSelectedManualCategoryId("");
                    }}
                  >
                    V2Ray
                  </Button>
                )}
                {enabledProviders.includes("VMESS") && (
                  <Button
                    variant={providerType === "VMESS" ? "contained" : "outlined"}
                    onClick={() => {
                      setProviderType("VMESS");
                      setSelectedPlan("");
                      setSelectedManualCategoryId("");
                    }}
                  >
                    VMess
                  </Button>
                )}
                {enabledProviders.includes("SSH") && (
                  <Button
                    variant={providerType === "SSH" ? "contained" : "outlined"}
                    onClick={() => {
                      setProviderType("SSH");
                      setSelectedPlan("");
                      setSelectedManualCategoryId("");
                    }}
                  >
                    SSH
                  </Button>
                )}
              </Box>
            )}

            {providerType === "V2RAY" ? (
              <>
            <Typography variant="h6" gutterBottom>
              {isRenewMode 
                ? "پلن فعلی شما (می‌توانید تغییر دهید):"
                : "یکی از پلن‌های زیر را انتخاب کنید:"
              }
            </Typography>
            <Grid container spacing={3}>
              {planTypes
                .filter((plan) => {
                  // Filter out disabled plans based on settings
                  if (!settings) return true;

                  switch (plan.id) {
                    case SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED:
                      return (settings as any).gigLimitTimeUnlimitedEnabled;
                    case SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT:
                      return (settings as any).gigUnlimitedTimeLimitEnabled;
                    case SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT:
                      return (settings as any).gigLimitTimeLimitEnabled;
                    default:
                      return true;
                  }
                })
                .map((plan) => (
                  <Grid item xs={12} md={4} key={plan.id}>
                    <SC.PlanCard
                      isSelected={selectedPlan === plan.id}
                      onClick={() => handlePlanSelect(plan.id)}
                    >
                      <SC.PlanCardContent>
                        <SC.PlanHeader>
                          <SC.PlanIcon>{plan.icon}</SC.PlanIcon>
                          <SC.PlanTitle>
                            <Typography variant="h6">{plan.title}</Typography>
                          </SC.PlanTitle>
                        </SC.PlanHeader>
                        <SC.PlanDescription>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            paragraph
                          >
                            {plan.description}
                          </Typography>
                        </SC.PlanDescription>
                        <SC.PlanFeatures>
                          {plan.features.map((feature, index) => (
                            <Chip
                              key={index}
                              label={feature}
                              size="small"
                              sx={{ m: 0.5 }}
                            />
                          ))}
                        </SC.PlanFeatures>
                        {settings && (
                          <SC.PlanPrice>
                            <Typography variant="body2" color="primary.main">
                              قیمت پایه:{" "}
                              {getBasePrice(plan.id).toLocaleString()} تومان
                              {plan.id ===
                                SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED &&
                                " در هر گیگابایت"}
                              {plan.id ===
                                SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT &&
                                " در هر ماه"}
                              {plan.id ===
                                SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT &&
                                " در هر گیگابایت"}
                            </Typography>
                          </SC.PlanPrice>
                        )}
                      </SC.PlanCardContent>
                    </SC.PlanCard>
                  </Grid>
                ))}
            </Grid>
            <br />

            {selectedPlan && (
              <SC.PlanConfigurationContainer>
                {isRenewMode && (
                  <CustomAlert severity="info">
                    پلن فعلی شما برای تمدید انتخاب شده است. می‌توانید پلن یا تنظیمات را تغییر دهید.
                  </CustomAlert>
                )}
                <SC.PlanConfigurationGrid>
                  {/* GIG_LIMIT_TIME_UNLIMITED: Gig limit dropdown (10-100) */}
                  {selectedPlan ===
                    SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>حجم (گیگابایت)</InputLabel>
                        <Select
                          value={gigLimit}
                          label="حجم (گیگابایت)"
                          onChange={(e) => setGigLimit(Number(e.target.value))}
                        >
                          {getGigLimitOptions().map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {/* GIG_UNLIMITED_TIME_LIMIT: Time limit dropdown (1-6 months) */}
                  {selectedPlan ===
                    SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>مدت زمان (ماه)</InputLabel>
                        <Select
                          value={timeLimit}
                          label="مدت زمان (ماه)"
                          onChange={(e) => setTimeLimit(Number(e.target.value))}
                        >
                          {getTimeLimitOptions().map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {/* GIG_LIMIT_TIME_LIMIT: Gig limit dropdown (10-100) and disabled time field (always 1) */}
                  {selectedPlan ===
                    SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>حجم (گیگابایت)</InputLabel>
                          <Select
                            value={gigLimit}
                            label="حجم (گیگابایت)"
                            onChange={(e) =>
                              setGigLimit(Number(e.target.value))
                            }
                          >
                            {getGigLimitOptions().map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="مدت زمان (ماه)"
                          value={1}
                          disabled
                          helperText="مدت زمان برای این پلن همیشه 1 ماه است"
                        />
                      </Grid>
                    </>
                  )}
                </SC.PlanConfigurationGrid>

                {/* Final Price Calculation */}
                {selectedPlan &&
                  settings &&
                  (gigLimit > 0 || timeLimit > 0) && (
                    <SC.FinalPriceContainer>
                      <SC.FinalPriceTitle>
                        <Typography variant="h6" color="primary.main">
                          قیمت نهایی
                        </Typography>
                      </SC.FinalPriceTitle>
                      <SC.FinalPriceCalculation>
                        <Typography variant="body1">
                          {selectedPlan ===
                            SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_UNLIMITED &&
                            `${getBasePrice().toLocaleString()} × ${gigLimit} گیگابایت = ${getCurrentPrice().toLocaleString()} تومان`}
                          {selectedPlan ===
                            SUBSCRIPTION_PLAN_TYPES.GIG_UNLIMITED_TIME_LIMIT &&
                            `${getBasePrice().toLocaleString()} × ${timeLimit} ماه = ${getCurrentPrice().toLocaleString()} تومان`}
                          {selectedPlan ===
                            SUBSCRIPTION_PLAN_TYPES.GIG_LIMIT_TIME_LIMIT &&
                            `${getBasePrice().toLocaleString()} × ${gigLimit} گیگابایت = ${getCurrentPrice().toLocaleString()} تومان (زمان: 1 ماه)`}
                        </Typography>
                      </SC.FinalPriceCalculation>
                    </SC.FinalPriceContainer>
                  )}
              </SC.PlanConfigurationContainer>
            )}
              </>
            ) : isInventoryProviderType(providerType) ? (
              <>
                <Typography variant="h6" gutterBottom>
                  یک تعرفه {providerType} انتخاب کنید:
                </Typography>
                <Grid container spacing={3}>
                  {(inventoryCategoriesData?.categories || []).map((category) => {
                    const outOfStock = category.availableCount === 0;
                    return (
                      <Grid item xs={12} md={4} key={category._id}>
                        <SC.PlanCard
                          isSelected={selectedManualCategoryId === category._id}
                          onClick={() => {
                            if (!outOfStock) {
                              setSelectedManualCategoryId(category._id);
                            }
                          }}
                          style={{
                            opacity: outOfStock ? 0.6 : 1,
                            cursor: outOfStock ? "not-allowed" : "pointer",
                          }}
                        >
                          <SC.PlanCardContent>
                            <Typography variant="h6">{category.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {formatCategoryLimit(category.gigLimit, "gig")}
                              {" · "}
                              {formatCategoryLimit(category.timeLimit, "time")}
                            </Typography>
                            <Typography variant="h6" color="primary.main" sx={{ mt: 2 }}>
                              {category.price.toLocaleString()} تومان
                            </Typography>
                            {outOfStock ? (
                              <Chip
                                label="تمام شده"
                                color="error"
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                موجودی: {category.availableCount}
                              </Typography>
                            )}
                          </SC.PlanCardContent>
                        </SC.PlanCard>
                      </Grid>
                    );
                  })}
                </Grid>
                {inventoryCategoriesData?.categories.length === 0 && (
                  <CustomAlert severity="info">
                    در حال حاضر تعرفه فعالی برای فروش وجود ندارد.
                  </CustomAlert>
                )}
              </>
            ) : null}
          </SC.PlanSelectionContainer>
        )}

        {/* Step 2: Payment Information */}
        {activeStep === 1 && settings && (
          <SC.PaymentInfoContainer>
            <Typography variant="h6" gutterBottom>
              اطلاعات پرداخت
            </Typography>
            <CustomAlert severity="info">
              لطفاً مبلغ {getCurrentPrice().toLocaleString()} تومان را به شماره
              کارت زیر واریز کنید:
            </CustomAlert>

            <SC.PaymentCard>
              <SC.PaymentInfoGrid>
                <SC.PaymentInfoItem>
                  <SC.PaymentInfoContent>
                    <SC.PaymentInfoLabel>
                      <Typography variant="body2" color="text.secondary">
                        شماره کارت
                      </Typography>
                    </SC.PaymentInfoLabel>
                    <SC.PaymentInfoValue>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Typography variant="h6" dir="ltr">
                          {formatCardNumber(settings.cardNumber)}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => copyCardNumber(settings.cardNumber)}
                          sx={{ minWidth: "auto", p: 0.5 }}
                        >
                          <ContentCopy />
                        </Button>
                      </div>
                    </SC.PaymentInfoValue>
                  </SC.PaymentInfoContent>
                </SC.PaymentInfoItem>
                <SC.PaymentInfoItem>
                  <SC.PaymentInfoContent>
                    <SC.PaymentInfoLabel>
                      <Typography variant="body2" color="text.secondary">
                        نام صاحب کارت
                      </Typography>
                    </SC.PaymentInfoLabel>
                    <SC.PaymentInfoValue>
                      <Typography variant="h6">
                        {settings.cardOwnerName}
                      </Typography>
                    </SC.PaymentInfoValue>
                  </SC.PaymentInfoContent>
                </SC.PaymentInfoItem>
                <SC.PaymentInfoItem>
                  <SC.PaymentInfoContent>
                    <SC.PaymentInfoLabel>
                      <Typography variant="body2" color="text.secondary">
                        مبلغ قابل پرداخت
                      </Typography>
                    </SC.PaymentInfoLabel>
                    <SC.PaymentInfoValue>
                      <Typography
                        className="final-price"
                        variant="h5"
                        color="primary.main"
                      >
                        {getCurrentPrice().toLocaleString("IR-fa")} تومان
                      </Typography>
                    </SC.PaymentInfoValue>
                  </SC.PaymentInfoContent>
                </SC.PaymentInfoItem>
              </SC.PaymentInfoGrid>
            </SC.PaymentCard>
          </SC.PaymentInfoContainer>
        )}

        {/* Step 3: Upload Receipt */}
        {activeStep === 2 && (
          <SC.UploadContainer>
            <Typography variant="h6" gutterBottom>
              آپلود فیش واریزی
            </Typography>
            <CustomAlert severity="warning">
              {isInventoryProviderType(providerType)
                ? "لطفاً فیش واریزی خود را آپلود کنید. پس از تأیید ادمین، اشتراک شما فعال خواهد شد."
                : "لطفاً فیش واریزی خود را آپلود کنید. پس از آپلود فیش اشتراک شما فعال خواهد شد."}
            </CustomAlert>

            <SC.UploadArea>
              <SC.UploadIcon>
                <Upload />
              </SC.UploadIcon>
              <SC.UploadText>
                <Typography variant="body1" gutterBottom>
                  فیش واریزی خود را انتخاب کنید
                </Typography>
              </SC.UploadText>
              <SC.UploadInput
                type="file"
                accept="image/*"
                onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
              />
              {receiptFile && (
                <SC.UploadFileName>
                  <Typography variant="body2" color="primary.main">
                    فایل انتخاب شده: {receiptFile.name}
                  </Typography>
                </SC.UploadFileName>
              )}
            </SC.UploadArea>
          </SC.UploadContainer>
        )}

        {/* Navigation Buttons */}
        <SC.NavigationContainer>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            قبلی
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              enabledProviders.length === 0 ||
              (providerType === "V2RAY" && !selectedPlan) ||
              (isInventoryProviderType(providerType) &&
                (!selectedManualCategoryId || isSelectedCategoryOutOfStock)) ||
              (activeStep === 2 && !receiptFile) ||
              creating ||
              renewing ||
              uploading
            }
          >
            {creating || renewing || uploading ? (
              <CircularProgress size={20} />
            ) : activeStep === steps.length - 1 ? (
              "تأیید و ارسال"
            ) : (
              "بعدی"
            )}
          </Button>
        </SC.NavigationContainer>
      </Paper>
    </SC.PurchaseSubscriptionContainer>
  );
};
